import { GetAssignmentsForAssignableResponse } from '@eigtech/assignments-types'
import { Book } from '@eigtech/contacts-types'
import { Corn, isCorn } from '@eigtech/shared-corn'
import { ensureClaimCorn } from '@eigtech/shared-corn-helper'
import {
  QueryClient,
  queryOptions,
  useQueries,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@eigtech/ui-shared-api'
import { getAssignmentsForAssignableQueryOptions } from '@eigtech/ui-shared-assignments'
import { last } from 'lodash-es'
import { useMemo } from 'react'

export type UseGetContactAssignmentForClaimProps = { contactId: string } & (
  | { claimNumber: string }
  | { book: Book | undefined }
)

export function useGetContactAssignmentForClaim(props: UseGetContactAssignmentForClaimProps) {
  const claimNumber =
    'claimNumber' in props
      ? props.claimNumber
      : (props.book?.metadata.type === 'claim' && last(props.book.bookId.split(':'))) || ''

  const claimCorn = ensureClaimCorn(claimNumber)

  return useGetContactAssignmentForEntity({ contactId: props.contactId, entityCorn: claimCorn })
}

export type UseGetContactsAssignmentForClaimProps = { contactIds: string[] } & (
  | { claimNumber: string }
  | { book: Book | undefined }
)

export function useGetContactsAssignmentForClaim(props: UseGetContactsAssignmentForClaimProps) {
  const claimNumber =
    'claimNumber' in props
      ? props.claimNumber
      : (props.book?.metadata.type === 'claim' && last(props.book.bookId.split(':'))) || ''

  const claimCorn = ensureClaimCorn(claimNumber)

  const queries = useGetContactsAssignmentForEntity({
    contactIds: props.contactIds,
    entityCorn: claimCorn,
  })
  const assignments = useMemo(() => queries.flatMap(({ data }) => data), [queries]).filter(
    (data) => data !== undefined
  )
  return { assignments, queries }
}

export type UseGetContactAssignmentForEntityProps = { contactId: string; entityCorn: Corn }

export const getContactAssignmentForEntityQueryOptions = (
  { contactId, entityCorn }: UseGetContactAssignmentForEntityProps,
  queryClient: QueryClient
) =>
  queryOptions({
    queryKey: [
      ...getAssignmentsForAssignableQueryOptions(entityCorn).queryKey,
      'contacts',
      contactId,
    ],
    async queryFn() {
      const data = await queryClient.fetchQuery(getAssignmentsForAssignableQueryOptions(entityCorn))

      return getContactAssignmentForEntity({ assignments: data, contactId })
    },
    enabled: !!entityCorn && isCorn(entityCorn),
  })

export function useGetContactAssignmentForEntity(props: UseGetContactAssignmentForEntityProps) {
  const queryClient = useQueryClient()

  return useQuery(getContactAssignmentForEntityQueryOptions(props, queryClient))
}

export type UseGetContactsAssignmentForEntityProps = { contactIds: string[]; entityCorn: Corn }

export function useGetContactsAssignmentForEntity(props: UseGetContactsAssignmentForEntityProps) {
  const queryClient = useQueryClient()

  return useQueries({
    queries: props.contactIds.map((contactId) =>
      getContactAssignmentForEntityQueryOptions(
        { contactId, entityCorn: props.entityCorn },
        queryClient
      )
    ),
  })
}

export function useGetContactAssignmentForEntitySuspense(
  props: UseGetContactAssignmentForEntityProps
) {
  const queryClient = useQueryClient()

  return useSuspenseQuery(getContactAssignmentForEntityQueryOptions(props, queryClient))
}

export type GetContactAssignmentForEntityProps = {
  assignments: GetAssignmentsForAssignableResponse | undefined
  contactId: string
}

export function getContactAssignmentForEntity({
  assignments,
  contactId,
}: GetContactAssignmentForEntityProps) {
  return assignments?.filter((assignment) => assignment.assignee.assigneeId === contactId)
}
